import { useState } from 'react';
import { motion } from 'framer-motion';
import ServiceCard from '@components/services/ServiceCard';
import BookingWizard from '@components/services/BookingWizard';
import { useServices } from '@hooks/useServices';
import { useBooking } from '@hooks/useBooking';
import Loading from '@components/common/Loading';
import toast from 'react-hot-toast';

/**
 * ServiceBookingPage
 * Features:
 * - BookingWizard multi-step flow
 * - API: POST /api/bookings
 * - Animations: step slide/fade
 * - Validation per step
 */

const ServiceBookingPage = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  
  const { services, loading } = useServices();
  const { createBooking } = useBooking();

  const handleBookService = (service) => {
    setSelectedService(service);
    setIsBookingModalOpen(true);
  };

  const handleConfirmBooking = async (bookingData) => {
    try {
      await createBooking(bookingData);
      toast.success('Booking confirmed successfully!');
    } catch (error) {
      toast.error('Failed to create booking. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professional security services tailored to your needs. Book online and get instant confirmation.
          </p>
        </div>

        {/* Services Grid */}
        {loading ? (
          <Loading text="Loading services..." />
        ) : (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.1,
                },
              },
            }}
          >
            {services.map((service) => (
              <motion.div
                key={service.id}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <ServiceCard {...service} onBook={handleBookService} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Why Choose Us Section */}
        <section className="mt-16 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-3xl font-bold text-center mb-8">Why Choose Our Services?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl mb-4">🔧</div>
              <h3 className="text-xl font-semibold mb-2">Expert Technicians</h3>
              <p className="text-gray-600">Certified professionals with years of experience</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-semibold mb-2">Fast Service</h3>
              <p className="text-gray-600">Same-day or next-day appointments available</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-4">✓</div>
              <h3 className="text-xl font-semibold mb-2">Guaranteed Work</h3>
              <p className="text-gray-600">12-month warranty on all installations</p>
            </div>
          </div>
        </section>
      </div>

      {/* Booking Wizard Modal */}
      <BookingWizard
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        services={services}
        onConfirm={handleConfirmBooking}
      />
    </div>
  );
};

export default ServiceBookingPage;

